import { useNavigation } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import LabeledHorizontalCardsList from 'components/common/LabeledHorizontalCardsList';
import Icon from 'components/icons/Icon';
import ParkingContext from 'context/ParkingContext';
import useGetMatchingPermitsForParkingZone from 'hooks/useGetMatchingPermitsForParkingZone';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MyPermitCard from 'screens/permit/MyPermitCard';
import { RootNavProps } from 'types/navigation';
import ZoneDetailsRow from './ZoneDetailsRow';
import { useQuery } from '@apollo/client';
import { READ_CUSTOMER_PMCS_DETAILS } from 'graphql/queries/readCustomerPmcsDetails';
import { Query, QueryReadCustomerPmcsArgs } from 'types/generatedSchemaTypes';
import LoyaltyAdvertisementCard from 'screens/permit/LoyaltyAdvertisementCard';

const useGetLoyaltyPmcs = (parkingZoneUid: string | undefined) => {
  const { data: pmcData } = useQuery<Query, QueryReadCustomerPmcsArgs>(
    READ_CUSTOMER_PMCS_DETAILS,
    {
      variables: {
        input: {
          parkingZoneUids: parkingZoneUid ? [parkingZoneUid] : [],
          permitType: 'LOYALTY',
        },
      },
      skip: !parkingZoneUid,
      errorPolicy: 'all',
    }
  );

  return pmcData?.readCustomerPmcs;
};

const ParkingLoyaltyPermits = ({
  showLoyaltyCallback,
  zoneInfoView,
}: {
  showLoyaltyCallback?: (show: boolean) => void;
  zoneInfoView?: boolean;
}) => {
  const { parkingZoneResult: parkingZone } = useContext(ParkingContext);

  const uid = parkingZone?.data?.parkZone?.uid;
  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();

  const {
    groupedMatchingPermits: { LOYALTY: loyaltyPermits },
  } = useGetMatchingPermitsForParkingZone(uid, ['LOYALTY']);

  useEffect(
    () => showLoyaltyCallback?.(loyaltyPermits?.length > 0),
    [loyaltyPermits]
  );

  const navigateToPermits = () =>
    navigate('permits', {
      permitType: 'LOYALTY',
      parkZoneUid: uid,
    });

  const pmcData = useGetLoyaltyPmcs(uid);

  if (!loyaltyPermits || loyaltyPermits?.length === 0) {
    // advertisement to customer because the person doesnt have the permit
    return (
      <LabeledHorizontalCardsList
        listData={pmcData ?? []}
        cardComponent={LoyaltyAdvertisementCard}
        cardComponentOptions={{ showLabel: true }}
        testIdentifiersPrefix="loyalty-permits"
        bold={false}
      />
    );
  }

  if (zoneInfoView) {
    return (
      <ZoneDetailsRow
        chevronColor={theme.colors.black}
        onPress={navigateToPermits}
        title={t('services.loyaltyPermits')}
        icon={<Icon name={'Account-Outlined'} />}
      />
    );
  }

  return (
    <LabeledHorizontalCardsList
      listData={loyaltyPermits}
      cardComponent={MyPermitCard}
      cardComponentOptions={{ showLabel: true }}
      testIdentifiersPrefix="loyalty-permits"
      bold={false}
    />
  );
};

export default ParkingLoyaltyPermits;
